import React, { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {FileText, Download, Loader2, BookMarked} from 'lucide-react'
import { usePublications } from '@/hooks/usePublications'
import EditableText from '@/components/editable/EditableText'

interface CategoryMeta {
  id: string
  label: string
  tag: string
  color: string
  desc: string
}

const CATEGORIES: CategoryMeta[] = [
  {
    id: 'rapports',
    label: 'Rapports & Études',
    tag: 'Recherche',
    color: '#C9E26A',
    desc: "Publications analytiques issues des expérimentations terrain : bilans d'impact, études de cas, rapports de transformation.",
  },
  {
    id: 'manifestes',
    label: 'Manifestes & Positions',
    tag: 'Vision',
    color: '#C8A24A',
    desc: "Textes de fond qui affirment nos convictions : manifestes programmatiques, prises de position sur la transformation et la création.",
  },
  {
    id: 'guides',
    label: 'Guides & Méthodes',
    tag: 'Pratique',
    color: '#C9E26A',
    desc: "Outils concrets et méthodologies reproductibles : guides pédagogiques, protocoles d'immersion, boîtes à outils.",
  },
  {
    id: 'lettres',
    label: 'Lettres & Newsletters',
    tag: 'Communauté',
    color: '#C8A24A',
    desc: "Correspondances périodiques adressées à notre communauté : actualités des pôles, retours d'expérience, récits de terrain.",
  },
  {
    id: 'medias',
    label: 'Contenus & Médias',
    tag: 'Création',
    color: '#C9E26A',
    desc: "Productions audiovisuelles et numériques : documentaires, podcasts, capsules pédagogiques, expositions numériques.",
  },
  {
    id: 'recherche',
    label: 'Recherche-Action',
    tag: 'Académique',
    color: '#C8A24A',
    desc: "Travaux co-produits avec des chercheurs et universités : articles académiques, notes de recherche, expérimentations documentées.",
  },
]

const CATEGORY_MAP = new Map(CATEGORIES.map((c) => [c.id, c]))

function formatDate(iso?: string): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  } catch {
    return ''
  }
}

const Publications: React.FC = () => {
  const { publications, loading, fetchPublications } = usePublications()
  const [filter, setFilter] = useState<string>('all')

  useEffect(() => {
    fetchPublications(true)
  }, [fetchPublications])

  // Catégories réellement présentes dans les données publiées
  const availableCategories = useMemo(() => {
    const present = new Set(publications.map((p) => p.category))
    return CATEGORIES.filter((c) => present.has(c.id))
  }, [publications])

  const filtered = useMemo(() => {
    if (filter === 'all') return publications
    return publications.filter((p) => p.category === filter)
  }, [publications, filter])

  return (
    <section id="publications" className="relative bg-[#1C3350] py-32 lg:py-44 overflow-hidden">
      {/* Background glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(900px 500px at 80% 50%, rgba(201,226,106,0.07), transparent 65%), radial-gradient(700px 400px at 10% 80%, rgba(200,162,74,0.06), transparent 60%)',
        }}
      />

      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-16">
          <div className="lg:col-span-4 reveal">
            <div className="text-[10px] tracking-luxe text-[#C8A24A] mb-4">
              <EditableText as="span" contentKey="publications.section.kicker" section="Publications" label="Numéro de section">07 — PUBLICATIONS</EditableText>
            </div>
            <div className="divider-gold w-24 mb-6" />
            <div className="text-[12px] tracking-mid uppercase text-[#C9BFA8]">
              <EditableText as="span" contentKey="publications.eyebrow" section="Publications" label="Sous-titre">Savoirs & Création</EditableText>
            </div>
          </div>
          <div className="lg:col-span-8 reveal">
            <h3 className="font-serif-display text-[36px] sm:text-[48px] lg:text-[60px] leading-[1.05] text-[#E8DCC4] text-balance">
              <EditableText contentKey="publications.title" section="Publications" label="Titre principal" multiline as="span">
                Ce que nous créons, nous le partageons.
              </EditableText>
            </h3>
            <EditableText
              as="p"
              contentKey="publications.intro"
              section="Publications"
              label="Introduction Publications"
              multiline
              className="mt-8 block max-w-3xl text-[16px] leading-[1.75] text-[#C9BFA8]"
            >
              Le Centre de Transformation produit des connaissances vivantes — nées sur le terrain, documentées avec soin et diffusées pour amplifier l'impact au-delà de nos murs. Découvrez nos dernières publications.
            </EditableText>
          </div>
        </div>

        {/* Filtres par catégorie */}
        {availableCategories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-12 reveal">
            <button
              onClick={() => setFilter('all')}
              className={`text-[10px] tracking-luxe uppercase px-4 py-2 rounded-full border transition-all duration-300 ${
                filter === 'all'
                  ? 'border-[#C9E26A] text-[#C9E26A] bg-[#C9E26A]/10'
                  : 'border-[#E8DCC4]/20 text-[#C9BFA8] hover:border-[#E8DCC4]/40'
              }`}
            >
              <EditableText as="span" contentKey="publications.filter.all" section="Publications" label="Filtre — Tout">Tout</EditableText>
            </button>
            {availableCategories.map((c) => (
              <button
                key={c.id}
                onClick={() => setFilter(c.id)}
                className="text-[10px] tracking-luxe uppercase px-4 py-2 rounded-full border transition-all duration-300"
                style={{
                  borderColor: filter === c.id ? c.color : 'rgba(232,220,196,0.20)',
                  color: filter === c.id ? c.color : '#C9BFA8',
                  backgroundColor: filter === c.id ? `${c.color}1A` : 'transparent',
                }}
              >
                <EditableText as="span" contentKey={`publications.category.${c.id}.label`} section="Publications" label={`Catégorie — ${c.label}`}>{c.label}</EditableText>
              </button>
            ))}
          </div>
        )}

        {/* État : chargement */}
        {loading && (
          <div className="flex items-center justify-center py-24 text-[#C9BFA8]">
            <Loader2 className="w-6 h-6 animate-spin mr-3 text-[#C9E26A]" />
            Chargement des publications…
          </div>
        )}

        {/* État : vide */}
        {!loading && publications.length === 0 && (
          <div className="text-center py-24 border border-dashed border-[#E8DCC4]/15 rounded-2xl reveal">
            <BookMarked className="w-10 h-10 mx-auto mb-5 text-[#C8A24A]/60" />
            <p className="font-serif-display text-[24px] text-[#E8DCC4] mb-3">
              <EditableText as="span" contentKey="publications.empty.title" section="Publications" label="État vide — titre">Nos premières publications arrivent bientôt.</EditableText>
            </p>
            <p className="text-[14px] text-[#C9BFA8] max-w-md mx-auto">
              <EditableText as="span" contentKey="publications.empty.desc" section="Publications" label="État vide — description" multiline>Les rapports, manifestes et guides issus de nos pôles seront publiés ici prochainement.</EditableText>
            </p>
          </div>
        )}

        {/* Grille de publications */}
        {!loading && filtered.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[rgba(232,220,196,0.10)]">
            <AnimatePresence mode="popLayout">
              {filtered.map((pub, i) => {
                const meta = CATEGORY_MAP.get(pub.category)
                const color = meta?.color || '#C9E26A'
                const tag = pub.tag || meta?.tag || 'Publication'
                return (
                  <motion.article
                    key={pub._id}
                    layout
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
                    className="relative bg-[#1C3350] p-10 lg:p-12 group overflow-hidden flex flex-col"
                  >
                    {/* Accent line top */}
                    <span
                      className="absolute top-0 left-0 h-[2px] w-0 group-hover:w-full transition-all duration-500 ease-out"
                      style={{ backgroundColor: color }}
                    />

                    {/* Cover ou icône */}
                    {pub.coverUrl ? (
                      <div className="mb-6 aspect-[4/3] overflow-hidden rounded-sm">
                        <img
                          src={pub.coverUrl}
                          alt={pub.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                    ) : (
                      <div className="mb-6 text-[#C9BFA8] group-hover:text-[color:var(--accent)]" style={{ ['--accent' as string]: color }}>
                        <FileText className="w-8 h-8" />
                      </div>
                    )}

                    {/* Tag */}
                    <div
                      className="inline-flex self-start text-[9px] tracking-luxe uppercase px-3 py-1 rounded-full mb-5 border"
                      style={{ color, borderColor: `${color}40`, backgroundColor: `${color}12` }}
                    >
                      {tag}
                    </div>

                    {/* Titre */}
                    <h4 className="font-serif-display text-[22px] lg:text-[26px] leading-[1.2] text-[#E8DCC4] mb-3 group-hover:text-white transition-colors duration-300">
                      {pub.title}
                    </h4>

                    {/* Description */}
                    <p className="text-[14px] leading-[1.7] text-[#C9BFA8] flex-1">{pub.description}</p>

                    {/* Footer carte */}
                    <div className="mt-6 pt-5 border-t border-[rgba(232,220,196,0.12)] flex items-center justify-between gap-4">
                      <span className="text-[11px] tracking-mid uppercase text-[#C9BFA8]/50">
                        {formatDate(pub.publishedDate) || meta?.label}
                      </span>
                      {pub.fileUrl && (
                        <a
                          href={pub.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-[11px] tracking-mid uppercase transition-colors"
                          style={{ color }}
                        >
                          <Download className="w-3.5 h-3.5" />
                          Télécharger
                        </a>
                      )}
                    </div>
                  </motion.article>
                )
              })}
            </AnimatePresence>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-20 lg:mt-24 text-center reveal">
          <div className="divider-lime w-24 mx-auto mb-8" />
          <EditableText
            as="p"
            contentKey="publications.cta"
            section="Publications"
            label="Phrase de conclusion Publications"
            multiline
            className="block font-serif-display text-[22px] lg:text-[30px] italic text-[#E8DCC4] max-w-3xl mx-auto leading-[1.5]"
          >
            Chaque publication est un acte de transmission — un fragment de notre écosystème offert à ceux qui construisent demain.
          </EditableText>
          <a
            href="#contact"
            className="inline-flex items-center gap-3 mt-10 px-8 py-4 text-[11px] tracking-luxe uppercase border border-[#C8A24A]/40 text-[#C8A24A] hover:bg-[#C8A24A]/10 hover:border-[#C8A24A] transition-all duration-300 rounded-sm"
          >
            <EditableText as="span" contentKey="publications.ctaLink" section="Publications" label="Lien — Recevoir nos publications">Recevoir nos publications</EditableText>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        </div>
      </div>
    </section>
  )
}

export default Publications
